// Knowledge Base — single briefing viewer.
//
// Renders the 4 sections (Trends / Vendor / Clinic / Threats) of a stored
// briefing + an "View email HTML" button that opens the rendered email body
// in a new tab.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Brain,
  Calendar,
  Mail,
  ExternalLink,
  TrendingUp,
  Network,
  Stethoscope,
  Radar,
  GitCompare,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  FileLock2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function Section({ title, icon: Icon, accent, summary, children }) {
  const ringCls = {
    sky: "ring-sky-500/15",
    emerald: "ring-emerald-500/15",
    indigo: "ring-indigo-500/15",
    rose: "ring-rose-500/15",
  }[accent];
  const textCls = {
    sky: "text-sky-400",
    emerald: "text-emerald-400",
    indigo: "text-indigo-400",
    rose: "text-rose-400",
  }[accent];
  return (
    <section className={`rounded-2xl border border-white/10 bg-slate-950/60 p-5 ring-1 ${ringCls}`}>
      <div className={`flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-semibold ${textCls}`}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      {summary && (
        <p className="mt-3 text-sm leading-6 text-slate-200 whitespace-pre-wrap">{summary}</p>
      )}
      {children}
    </section>
  );
}

function ItemList({ items, fields }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="mt-3 space-y-2">
      {items.map((it, i) => (
        <li
          key={`${fields[0]}-${i}`}
          className="rounded-xl border border-white/10 bg-slate-900/40 p-3"
        >
          <div className="text-sm font-semibold text-slate-100">{it[fields[0]]}</div>
          {fields.slice(1).map((k) => {
            const v = it[k];
            if (!v) return null;
            const tone =
              k === "action" || k === "next_step"
                ? "text-sky-300"
                : k === "counter"
                ? "text-emerald-300"
                : k === "threat_level"
                ? "text-rose-300"
                : "text-slate-300";
            const label =
              k === "action" || k === "next_step"
                ? "Action"
                : k === "counter"
                ? "Counter"
                : k === "threat_level"
                ? "Threat"
                : k === "why_now"
                ? "Why now"
                : k === "recommended_program"
                ? "Suggested program"
                : null;
            return (
              <div key={k} className={`mt-1 text-xs ${tone}`}>
                {label && <span className="font-semibold mr-1">{label}:</span>}
                {String(v)}
              </div>
            );
          })}
        </li>
      ))}
    </ul>
  );
}

function PollUntilReady({ reportId, onReady }) {
  useEffect(() => {
    let cancelled = false;
    const deadline = Date.now() + 180_000;
    const tick = async () => {
      if (cancelled || Date.now() > deadline) return;
      try {
        const r = await api.get(`/kb/reports/${reportId}`);
        if (cancelled) return;
        if (r.data.status === "ready" || r.data.status === "failed") {
          onReady(r.data);
          return;
        }
      } catch {
        // ignore transient errors
      }
      setTimeout(tick, 4000);
    };
    const t = setTimeout(tick, 4000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [reportId, onReady]);
  return null;
}

function DeltaPill({ value, label }) {
  if (value == null) return null;
  const isObj = typeof value === "object";
  if (isObj) {
    return (
      <span className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-[11px] text-slate-300">
        {label}: <span className="text-slate-100">{String(value.from ?? "—")}</span>{" "}
        →{" "}
        <span className="text-slate-100">{String(value.to ?? "—")}</span>
      </span>
    );
  }
  const n = Number(value);
  const cls =
    n > 0
      ? "bg-emerald-500/15 text-emerald-300"
      : n < 0
      ? "bg-rose-500/15 text-rose-300"
      : "bg-slate-500/15 text-slate-400";
  const Icon = n > 0 ? ArrowUpRight : n < 0 ? ArrowDownRight : Minus;
  const sign = n > 0 ? "+" : "";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      <Icon className="h-3 w-3" />
      {label}: {sign}
      {n}
    </span>
  );
}

function ChangeRow({ change }) {
  const colors = {
    new: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    removed: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    escalated: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    "de-escalated": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    stable: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  };
  const cls = colors[change.direction] || colors.stable;
  return (
    <li className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
          {change.direction || "change"}
        </span>
        <span className="rounded-full bg-slate-900/80 border border-white/10 px-2 py-0.5 text-[10px] text-slate-300">
          {change.area}
        </span>
      </div>
      {change.detail && <p className="mt-2 text-sm text-slate-100">{change.detail}</p>}
      {change.action && (
        <p className="mt-1 text-xs text-violet-300">
          <span className="font-semibold">Action:</span> {change.action}
        </p>
      )}
    </li>
  );
}

function DiffPanel({ diff, loading, onClose }) {
  if (loading) {
    return (
      <section
        data-testid="kb-diff-panel"
        className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-5"
      >
        <div className="flex items-center gap-2 text-sm text-slate-200">
          <Loader2 className="h-4 w-4 animate-spin text-violet-300" />
          Claude is comparing this briefing against the previous one…
        </div>
      </section>
    );
  }
  if (!diff) return null;
  if (!diff.previous) {
    return (
      <section
        data-testid="kb-diff-panel"
        className="rounded-2xl border border-white/10 bg-slate-950/60 p-5"
      >
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-violet-300" /> Compare with previous
          </div>
          <button
            onClick={onClose}
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            close
          </button>
        </div>
        <p className="mt-2 text-sm text-slate-400">
          This is your first briefing — nothing to compare against yet.
        </p>
      </section>
    );
  }

  const countsDelta = diff.counts_delta || {};
  const statsDelta = diff.vendor_stats_delta || {};
  const topChanges = diff.top_vendor_changes || { added: [], removed: [] };
  const narrative = diff.narrative;

  return (
    <section
      data-testid="kb-diff-panel"
      className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 via-slate-950/70 to-slate-950/70 p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-violet-300 flex items-center gap-1.5">
            <GitCompare className="h-3 w-3" /> Briefing diff · Claude Sonnet 4.5
          </div>
          <div className="mt-1 text-sm text-slate-200">
            {diff.previous.period} ({diff.previous.generated_at?.slice(0, 10)}){" "}
            <span className="mx-1 text-slate-500">→</span>{" "}
            <span className="font-semibold text-slate-100">
              {diff.current.period} ({diff.current.generated_at?.slice(0, 10)})
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          close
        </button>
      </div>

      {narrative?.summary && (
        <p className="mt-4 text-sm leading-6 text-slate-100">{narrative.summary}</p>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-300">
            Records delta
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.keys(countsDelta).length === 0 ? (
              <span className="text-xs text-slate-500">No change.</span>
            ) : (
              Object.entries(countsDelta).map(([k, v]) => (
                <DeltaPill key={k} value={v} label={k} />
              ))
            )}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-300">
            Vendor scorecard delta
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.keys(statsDelta).length === 0 ? (
              <span className="text-xs text-slate-500">No change.</span>
            ) : (
              Object.entries(statsDelta).map(([k, v]) => (
                <DeltaPill key={k} value={v} label={k} />
              ))
            )}
          </div>
          {(topChanges.added?.length > 0 || topChanges.removed?.length > 0) && (
            <div className="mt-3 text-[11px] text-slate-400 space-y-1">
              {topChanges.added?.length > 0 && (
                <div>
                  <span className="text-emerald-300">+ Top:</span>{" "}
                  <span className="text-slate-200">{topChanges.added.join(", ")}</span>
                </div>
              )}
              {topChanges.removed?.length > 0 && (
                <div>
                  <span className="text-rose-300">− Top:</span>{" "}
                  <span className="text-slate-400">{topChanges.removed.join(", ")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {narrative?.changes?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-slate-300 mb-2">
            Key changes
          </div>
          <ul data-testid="kb-diff-changes" className="space-y-2">
            {narrative.changes.map((c, i) => (
              <ChangeRow key={`${c.detail}-${i}`} change={c} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default function AdminKnowledgeBaseReport() {
  const { reportId } = useParams();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);

  const loadDiff = async () => {
    setDiffOpen(true);
    if (diff) return;
    setDiffLoading(true);
    try {
      const res = await api.get(`/kb/reports/${reportId}/diff`);
      setDiff(res.data);
    } catch (e) {
      toast.error(formatApiError(e, "Diff failed"));
      setDiffOpen(false);
    } finally {
      setDiffLoading(false);
    }
  };

  const [investorBusy, setInvestorBusy] = useState(null); // "pdf" | "preview" | null

  const downloadInvestorPdf = async () => {
    setInvestorBusy("pdf");
    toast.info("Claude is abstracting + Playwright is rendering… ~45-60s");
    try {
      const token = localStorage.getItem("nodera_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kb/reports/${reportId}/investor-pdf`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Nodera-Intelligence-Briefing-${r.period?.replace(/\s+/g, "-")}-Investor.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Investor PDF downloaded");
    } catch (e) {
      toast.error(`PDF generation failed: ${e.message}`);
    } finally {
      setInvestorBusy(null);
    }
  };

  const previewInvestorHtml = async () => {
    setInvestorBusy("preview");
    try {
      const token = localStorage.getItem("nodera_token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/kb/reports/${reportId}/investor-html`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setInvestorBusy(null);
    }
  };

  useEffect(() => {
    api
      .get(`/kb/reports/${reportId}`)
      .then((res) => setR(res.data))
      .catch((e) => toast.error(formatApiError(e, "Could not load briefing")))
      .finally(() => setLoading(false));
  }, [reportId]);

  const openEmailHtml = () => {
    // The preview endpoint requires the admin's bearer token, so we fetch
    // the HTML body via authed XHR then open it in a new tab as a blob.
    const token = localStorage.getItem("nodera_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kb/reports/${reportId}/preview-html`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.text())
      .then((html) => {
        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast.error(`Preview failed: ${e.message}`));
  };

  if (loading || !r) {
    return (
      <div className="min-h-screen bg-slate-950 p-10 text-sm text-slate-300">Loading briefing…</div>
    );
  }

  if (r.status === "generating") {
    return (
      <div
        data-testid={`kb-report-page-${reportId}`}
        className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-10 text-center"
      >
        <Brain className="mx-auto h-10 w-10 text-sky-400 animate-pulse" />
        <h2 className="mt-4 text-xl font-semibold text-slate-100">
          Claude is researching your briefing…
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          This typically takes 60-90s. The page will auto-refresh.
        </p>
        <PollUntilReady reportId={reportId} onReady={setR} />
      </div>
    );
  }

  if (r.status === "failed") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-10 text-center">
        <h2 className="text-xl font-semibold text-rose-300">Briefing generation failed</h2>
        <p className="mt-2 text-sm text-slate-400">{r.error || "Unknown error"}</p>
        <Link
          to="/admin/knowledge-base/reports"
          className="mt-4 inline-block text-sm text-sky-300 hover:underline"
        >
          ← Back to briefings
        </Link>
      </div>
    );
  }

  const counts = r.counts || {};
  return (
    <div
      data-testid={`kb-report-page-${reportId}`}
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 space-y-6"
    >
      <Link
        to="/admin/knowledge-base/reports"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-slate-400 hover:text-slate-200"
      >
        <ArrowLeft className="h-3 w-3" /> All briefings
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sky-400 flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> {r.period}
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-100 flex items-center gap-2">
            <Brain className="h-6 w-6 text-sky-400" /> Nodera Intelligence Briefing
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            Generated {new Date(r.generated_at).toLocaleString()} · {r.model || "claude-sonnet-4-5"}
            {r.delivered_to && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-300">
                <Mail className="h-3 w-3" /> emailed to {r.delivered_to}
              </span>
            )}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
            {["programs", "vendors", "clinics", "workflows", "marketing", "economics", "competitors"].map((k) => (
              <span
                key={k}
                className="rounded-full border border-white/10 bg-slate-900/60 px-2 py-0.5 text-slate-300"
              >
                {k}: <span className="text-slate-100 font-semibold">{counts[k] ?? 0}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="kb-report-compare"
            onClick={loadDiff}
            className="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-sm font-semibold text-violet-200 hover:bg-violet-500/20"
          >
            <GitCompare className="h-3.5 w-3.5" /> Compare with previous
          </button>
          <button
            data-testid="kb-report-investor-preview"
            onClick={previewInvestorHtml}
            disabled={investorBusy !== null}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
          >
            {investorBusy === "preview" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileLock2 className="h-3.5 w-3.5" />
            )}
            Investor preview
          </button>
          <button
            data-testid="kb-report-investor-pdf"
            onClick={downloadInvestorPdf}
            disabled={investorBusy !== null}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-400 disabled:opacity-60"
          >
            {investorBusy === "pdf" ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating PDF…
              </>
            ) : (
              <>
                <FileLock2 className="h-3.5 w-3.5" /> Investor PDF
              </>
            )}
          </button>
          <button
            data-testid="kb-report-view-email"
            onClick={openEmailHtml}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-sm font-semibold text-sky-200 hover:bg-sky-500/20"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View email HTML
          </button>
        </div>
      </header>

      {/* Diff panel */}
      {diffOpen && <DiffPanel diff={diff} loading={diffLoading} onClose={() => setDiffOpen(false)} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section
          title="1 · Trends"
          icon={TrendingUp}
          accent="sky"
          summary={r.trends?.summary}
        >
          <ItemList items={r.trends?.items} fields={["title", "detail", "action"]} />
        </Section>

        <Section
          title="2 · Vendor Network Health"
          icon={Network}
          accent="emerald"
          summary={r.vendor_network?.summary}
        >
          {r.vendor_network?.stats && (
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded-full bg-slate-900 text-white px-2 py-0.5 font-semibold">
                {r.vendor_network.stats.total} total
              </span>
              <span className="rounded-full bg-emerald-500 text-white px-2 py-0.5 font-semibold">
                {r.vendor_network.stats.approved} approved
              </span>
              <span className="rounded-full bg-amber-500 text-white px-2 py-0.5 font-semibold">
                {r.vendor_network.stats.unscored} unscored
              </span>
              {r.vendor_network.stats.avg_scorecard != null && (
                <span className="rounded-full bg-sky-500 text-white px-2 py-0.5 font-semibold">
                  avg {r.vendor_network.stats.avg_scorecard}/10
                </span>
              )}
            </div>
          )}
          {r.vendor_network?.top_vendors?.length > 0 && (
            <div className="mt-2 text-xs text-slate-400">
              Top vendors: <span className="text-slate-200">{r.vendor_network.top_vendors.join(", ")}</span>
            </div>
          )}
          <ItemList items={r.vendor_network?.items} fields={["title", "detail", "action"]} />
        </Section>

        <Section
          title="3 · Clinic Opportunities"
          icon={Stethoscope}
          accent="indigo"
          summary={r.clinic_opportunities?.summary}
        >
          <ItemList
            items={r.clinic_opportunities?.items}
            fields={["clinic", "recommended_program", "why_now", "next_step"]}
          />
        </Section>

        <Section
          title="4 · Top Competitive Threats"
          icon={Radar}
          accent="rose"
          summary={r.competitive_threats?.summary}
        >
          <ItemList
            items={r.competitive_threats?.items}
            fields={["competitor", "threat_level", "detail", "counter", "action"]}
          />
        </Section>
      </div>
    </div>
  );
}
